import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { API_DOMAIN, MY_DOMAIN } from '../config/domain';

interface User {
  username: string;
  email: string;
  token: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (token: string, userData: Omit<User, 'token'>) => void;
  logout: () => void;
  loading: boolean;
  favorites: Set<string>;
  loadFavorites: () => Promise<void>;
  toggleFavorite: (phone: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  const loadFavorites = async (email?: string): Promise<void> => {
    const userEmail = email || localStorage.getItem('email') || '';
    if (!userEmail) return;
    try {
      const res = await fetch(`${API_DOMAIN}/api/v2/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer readesc',
        },
        body: JSON.stringify({
          action: 'search',
          model: 'site_preferred',
          driver: 'json',
          criteria: { user: { equal: userEmail } }
        })
      });
      const data = await res.json();
      const results: any[] = data?.results ?? [];
      const phones = results.map((r: any) => r.model).filter(Boolean) as string[];
      setFavorites(new Set(phones));
    } catch (_) {}
  };

  const toggleFavorite = async (phone: string): Promise<boolean> => {
    const cleanPhone = phone.trim();
    try {
      const res = await fetch(
        `${MY_DOMAIN}/user-favorites.php?phone=${encodeURIComponent(cleanPhone)}&ajax=1`,
        { credentials: 'include' }
      );
      const text = await res.text();
      const isNowFav = text.trim() === 'true';
      setFavorites(prev => {
        const next = new Set(prev);
        if (isNowFav) next.add(cleanPhone); else next.delete(cleanPhone);
        return next;
      });
      return isNowFav;
    } catch (_) {
      return false;
    }
  };

  useEffect(() => {
    // Check if user is already logged in on app start
    const token = authService.getToken();
    if (token) {
      // Set user as authenticated with the stored token
      // In a real app, you might want to validate the token with the server
      setUser({
        username: localStorage.getItem('username') || 'User',
        email: localStorage.getItem('email') || '',
        role: localStorage.getItem('role') || '',
        token
      });
      // Load favorites on init
      setTimeout(() => loadFavorites(), 0);
    }
    setLoading(false);
  }, []);

  const login = (token: string, userData: Omit<User, 'token'>) => {
    const userWithToken = { ...userData, token };
    setUser(userWithToken);
    authService.setToken(token);

    // Store user data in localStorage for persistence
    localStorage.setItem('username', userData.username);
    localStorage.setItem('email', userData.email);
    if (userData.role) localStorage.setItem('role', userData.role);
    // Load favorites after login
    setTimeout(() => loadFavorites(userData.email), 0);
  };

  const logout = () => {
    setUser(null);
    setFavorites(new Set());
    authService.logout();
    // Remove user data from localStorage
    localStorage.removeItem('username');
    localStorage.removeItem('email');
    localStorage.removeItem('role');
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
    favorites,
    loadFavorites,
    toggleFavorite,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
